/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "../api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authApi.session()
      .then((data) => setUser(data.isAuthenticated ? data.user : null))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (loginField, password) => {
    await authApi.csrf();
    const data = await authApi.login(loginField, password);
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (username, email, password1, password2) => {
    await authApi.csrf();
    const data = await authApi.signup(username, email, password1, password2);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Clear client state regardless of server response
    }
    setUser(null);
  }, []);

  const changePassword = useCallback(async (oldPassword, newPassword1, newPassword2) => {
    await authApi.csrf();
    return authApi.changePassword(oldPassword, newPassword1, newPassword2);
  }, []);

  const resetPassword = useCallback(async (email) => {
    await authApi.csrf();
    return authApi.resetPassword(email);
  }, []);

  const refreshUser = useCallback(async () => {
    const data = await authApi.session();
    setUser(data.isAuthenticated ? data.user : null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, signup, logout, changePassword, resetPassword, refreshUser }),
    [user, loading, login, signup, logout, changePassword, resetPassword, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
